import React, { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ColumnDef<T extends { id?: string | number }> = {
  key: keyof T;
  header: string;
  editable?: boolean;
  render?: (value: any, row: T, onRowChange: (patch: Partial<T>) => void) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortAccessor?: (row: T) => any;
};

interface DataTableProps<T extends { id?: string | number }> {
  data: T[];
  setData: (rows: T[]) => void;
  columns: ColumnDef<T>[];
  pageSize?: number;
  dense?: boolean;
  onRowClick?: (row: T) => void;
}

export function DataTable<T extends { id?: string | number }>({ data, setData, columns, pageSize = 10, dense = false, onRowClick }: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key?: keyof T; dir: "asc" | "desc" }>({ key: undefined, dir: "asc" });

  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const col = columns.find(c => c.key === sort.key);
    const accessor = col?.sortAccessor;
    const getVal = (row: T) => accessor ? accessor(row) : (row[sort.key!] as any);
    const cmp = (a: any, b: any) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va == null && vb == null) return 0;
      if (va == null) return -1;
      if (vb == null) return 1;
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: "base" });
    };
    const sorted = [...data].sort((a, b) => cmp(a, b));
    return sort.dir === "asc" ? sorted : sorted.reverse();
  }, [data, columns, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const start = (page - 1) * pageSize;
  const pageRows = useMemo(() => sortedData.slice(start, start + pageSize), [sortedData, start, pageSize]);

  const onCellChange = (rowIndex: number, patch: Partial<T>) => {
    setData(
      data.map((r, i) => (i === start + rowIndex ? { ...r, ...patch } : r))
    );
  };

  return (
    <div className="space-y-3">
      <div className="w-full overflow-x-auto rounded-md border border-border">
        <Table className={cn(dense && "text-sm")}>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={String(c.key)}
                  className={cn(dense ? "py-2 text-xs" : "min-w-[140px]", c.className, c.sortable && "cursor-pointer select-none")}
                  onClick={c.sortable ? () => setSort((prev) => ({ key: c.key, dir: prev.key === c.key && prev.dir === "asc" ? "desc" : "asc" })) : undefined}
                  aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <div className="flex items-center gap-1">
                    <span>{c.header}</span>
                    {sort.key === c.key && (
                      <span aria-hidden className="text-muted-foreground">{sort.dir === "asc" ? "▲" : "▼"}</span>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((row, ri) => (
              <TableRow key={(row.id ?? ri) as any} onClick={onRowClick ? () => onRowClick(row) : undefined} className={cn(onRowClick && "cursor-pointer hover:bg-accent/50") }>
                {columns.map((c) => {
                  const value = row[c.key];
                  const onRowChange = (patch: Partial<T>) => onCellChange(ri, patch);
                  return (
                    <TableCell key={String(c.key)} className={cn(dense && "py-2", c.className)}>
                      {c.render ? (
                        c.render(value, row, onRowChange)
                      ) : c.editable ? (
                        <Input
                          className={cn(dense && "h-8")}
                          value={String(value ?? "")}
                          onChange={(e) => onRowChange({ [c.key]: e.target.value } as Partial<T>)}
                        />
                      ) : (
                        <span className="text-foreground">{String(value ?? "-")}</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-muted-foreground">
          Página {page} de {totalPages}
        </span>
        <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
          Anterior
        </Button>
        <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
          Próxima
        </Button>
      </div>
    </div>
  );
}
